import { currentLocale, t, type Locale } from "../lib/i18n";
import { ApiError, rawRequest, type ApiOptions } from "./transport";

const STATUSES = ["reserved", "exchange_started", "token_received", "completed", "unknown", "failed", "expired", "superseded"] as const;
export interface AuthOperation {
  operation_id: string;
  status: typeof STATUSES[number];
  error_code: string | null;
  locale: Locale;
  return_path: string;
  send_deadline_at: string | null;
  resolved_generation: string | null;
  setup_status: string | null;
}

type RecoveryKind = "bootstrap" | "recover";
export type RecoveryReason = "unknown" | "failed" | "expired" | "superseded" | "conflict" | "unauthorized" | "redact" | "unavailable" | "poll_limit" | "missing" | "context_invalid" | "not_ready" | "ready" | "reauthorize";
export interface RecoveryState {
  phase: "idle" | "working" | "blocked" | "completed";
  operation: AuthOperation | null;
  reason: RecoveryReason | null;
  busy: boolean;
  manualCompletion: number;
  /** /api/meによる現在世代の準備状況。操作履歴がない店舗でも表示する。 */
  setupStatus?: string | null;
  /** 有効なJWTで既存Shopを再読できる場合、保存結果不明でもlocal閲覧を維持する。 */
  accessAvailable?: boolean;
  offlineTokenState?: string | null;
}

export class AuthRecoveryError extends Error {
  constructor() { super(t("接続状態を確認してください")); }
}

function validReturnPath(path: unknown): path is string {
  return typeof path === "string" && /^(?:\/(?:archive|diagnosis|plans)?|\/invoices\/[1-9][0-9]{0,19})$/.test(path);
}

function parseOperation(value: unknown): AuthOperation {
  const op = value as Partial<AuthOperation> | null;
  const nullableText = (v: unknown) => v === null || typeof v === "string";
  if (!op || typeof op.operation_id !== "string" || !/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(op.operation_id)
    || !STATUSES.includes(op.status as AuthOperation["status"])
    || !["ja", "en"].includes(op.locale as string) || !validReturnPath(op.return_path)
    || !nullableText(op.error_code) || !nullableText(op.setup_status) || !nullableText(op.resolved_generation)
    || !nullableText(op.send_deadline_at)
    || (op.send_deadline_at !== null && !Number.isFinite(Date.parse(op.send_deadline_at as string)))
    || (op.status === "completed" && !op.resolved_generation)) throw new AuthRecoveryError();
  return op as AuthOperation;
}

type Request = (path: string, options?: ApiOptions) => Promise<Response>;

/**
 * 1画面の通信を集約する。所有権や成功の根拠は常にserverで、storageには保存しない。
 * componentから独立しているためunmountは送信取消し/操作削除を意味しない。
 * 別tab/reloadは新しいinstanceからcurrentを読み、serverの同owner操作へ合流する。
 */
export function createAuthRecovery(request: Request = rawRequest) {
  let state: RecoveryState = { phase: "idle", operation: null, reason: null, busy: false, manualCompletion: 0 };
  const listeners = new Set<() => void>();
  let flight: Promise<void> | null = null;
  let initialRead: Promise<AuthOperation | null> | null = null;
  let automaticStarted = false;
  let lastKind: RecoveryKind | null = null;
  let polls = 0;
  let accessRevision = 0;
  let accessRevoked = false;
  let activeRunRevision: number | null = null;
  const completed = () => state.phase === "completed";

  function update(patch: Partial<RecoveryState>) {
    state = { ...state, ...patch };
    listeners.forEach((notify) => notify());
  }

  function block(reason: RecoveryReason) {
    const revoked = ["unauthorized", "redact", "missing", "not_ready", "reauthorize"].includes(reason);
    if (revoked) { accessRevision += 1; accessRevoked = true; }
    update({ phase: "blocked", reason, busy: false,
      ...(revoked ? { accessAvailable: false } : {}) });
  }

  function assertCurrentRun() {
    if (activeRunRevision !== null && activeRunRevision !== accessRevision) throw new AuthRecoveryError();
  }

  function acceptMeStatus(me: { install_setup_status?: string | null; offline_token_state?: string | null }, revision: number, explicit = false): boolean {
    if (revision !== accessRevision || (accessRevoked && !explicit)) return false;
    accessRevoked = false;
    update({ setupStatus: me.install_setup_status, offlineTokenState: me.offline_token_state, accessAvailable: true });
    return true;
  }

  async function current(): Promise<AuthOperation | null> {
    try {
      assertCurrentRun();
      const op = parseOperation(await (await request("/auth/operations/current")).json());
      assertCurrentRun();
      return op;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404 && error.detail === "auth_operation_not_found") return null;
      throw error;
    }
  }

  async function post(path: string, body?: unknown): Promise<AuthOperation> {
    assertCurrentRun();
    const op = parseOperation(await (await request(path, { method: "POST", ...(body === undefined ? {} : { body }) })).json());
    assertCurrentRun();
    return op;
  }

  async function verifyAccess(explicit = false): Promise<RecoveryKind | null> {
    assertCurrentRun();
    const revision = accessRevision;
    try {
      const me = await (await request("/api/me")).json();
      assertCurrentRun();
      if (!me || typeof me !== "object" || Array.isArray(me)) throw new AuthRecoveryError();
      // 直接開いたArchive/Diagnosisも、共通の権威readbackで準備/接続状態を得る。
      if (!acceptMeStatus(me, revision, explicit)) throw new AuthRecoveryError();
      return null;
    }
    catch (error) {
      if (error instanceof ApiError && error.status === 404 && error.detail === "shop_not_installed") return "bootstrap";
      if (error instanceof ApiError && error.status === 410 && error.detail === "shop_recovery_required") return "recover";
      throw error;
    }
  }

  async function showReadback(op: AuthOperation | null, manual: boolean) {
    assertCurrentRun();
    if (!op) {
      const kind = await verifyAccess(manual);
      if (!kind) {
        update({ phase: "completed", operation: null, reason: null, busy: false,
          manualCompletion: state.manualCompletion + (manual ? 1 : 0) });
        return;
      }
      lastKind = kind;
    }
    if (op?.status === "completed") {
      const kind = await verifyAccess(manual);
      if (kind) { lastKind = kind; update({ operation: op }); block("reauthorize"); return; }
    }
    show(op, manual);
  }

  function show(op: AuthOperation | null, manual: boolean) {
    assertCurrentRun();
    if (!op) { update({ operation: null }); block("missing"); return; }
    update({ operation: op });
    if (op.status === "completed") {
      update({ phase: "completed", reason: null, busy: false,
        manualCompletion: state.manualCompletion + (manual ? 1 : 0) });
    } else if (op.status === "reserved" || op.status === "token_received") block("ready");
    else if (op.status === "exchange_started") block("poll_limit");
    else block(op.status);
  }

  async function reserve(kind: RecoveryKind): Promise<AuthOperation> {
    const returnPath = window.location.pathname;
    if (!validReturnPath(returnPath)) { block("context_invalid"); throw new AuthRecoveryError(); }
    return post(`/auth/${kind}`, { locale: currentLocale(), return_path: returnPath });
  }

  async function drive(first: AuthOperation, manual: boolean) {
    let op: AuthOperation | null = first;
    // reservedを一度claimし、後で保存tokenを一度確定できる。どちらも再送しない。
    const resumed = new Set<string>();
    while (op) {
      assertCurrentRun();
      update({ operation: op, phase: "working", reason: null });
      if (op.status === "reserved" || op.status === "token_received") {
        const step = `${op.operation_id}:${op.status}`;
        if (resumed.has(step)) { block("not_ready"); return; }
        resumed.add(step);
        op = await post(`/auth/operations/${op.operation_id}/resume`);
        continue;
      }
      if (op.status !== "exchange_started") {
        if (manual && op.status === "completed") await showReadback(op, true);
        else show(op, manual);
        return;
      }
      if (polls >= 30 || (op.send_deadline_at !== null && Date.parse(op.send_deadline_at) <= Date.now())) {
        block("poll_limit"); return;
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 1_000));
      // 回数は画面全体で保持し、query retry/unmount/手動操作でリセットしない。
      polls += 1;
      op = await current();
    }
    show(null, manual);
  }

  function failure(error: unknown) {
    if (error instanceof ApiError) {
      if (error.status === 401) { block("unauthorized"); return; }
      if (error.status === 409) { update({ operation: null }); block("conflict"); return; }
      if (error.status === 423) { update({ operation: null }); block("redact"); return; }
      if (error.status === 422) { block("context_invalid"); return; }
    }
    if (state.reason !== "context_invalid") block("unavailable");
  }

  function run(work: () => Promise<void>): Promise<void> {
    if (flight) return flight;
    const revision = ++accessRevision;
    activeRunRevision = revision;
    update({ busy: true, phase: "working", reason: null, setupStatus: undefined });
    flight = work().catch(error => { if (revision === accessRevision) failure(error); }).finally(() => {
      flight = null;
      activeRunRevision = null;
      if (revision === accessRevision && state.busy) update({ busy: false });
    });
    return flight;
  }

  /** 起動時の補助readback。利用可能なShopの読取りを他ownerの内部更新で止めない。 */
  function observe(): Promise<void> {
    if (!initialRead) initialRead = current();
    const pending = initialRead;
    return pending.catch(error => {
      // 内部ownerの履歴は公開しないが、現在Shopのlocal閲覧状態は照合する。
      if (error instanceof ApiError && error.status === 409) return null;
      throw error;
    }).then(async (op) => {
      if (!automaticStarted && state.phase === "idle") {
        const kind = await verifyAccess();
        if (!kind && !automaticStarted && state.phase === "idle") {
          lastKind = "recover";
          if (op) show(op, false);
          else update({ phase: "completed", operation: null, reason: null, busy: false });
        }
      }
    }).catch(() => undefined).finally(() => {
      // 起動時のreadbackを後の再install判定へ流用しない。
      if (initialRead === pending) initialRead = null;
    });
  }

  async function ensure(kind: RecoveryKind): Promise<void> {
    update({ accessAvailable: false });
    while (flight) await flight;
    if (automaticStarted && completed()) return;
    if (state.phase === "blocked" || automaticStarted) throw new AuthRecoveryError();
    automaticStarted = true;
    lastKind = kind;
    await run(async () => {
      let op = await (initialRead ?? current());
      initialRead = null;
      if (op?.status === "completed") {
        const requiredKind = await verifyAccess();
        if (!requiredKind) { show(op, false); return; }
        kind = requiredKind;
        lastKind = kind;
        op = null;
      }
      await drive(op ?? await reserve(kind), false);
    });
    if (!completed()) throw new AuthRecoveryError();
  }

  /** 手動の再確認はGETのみ。unknownでも新たな取得へ接続しない。 */
  async function check(): Promise<void> {
    await run(async () => showReadback(await current(), true));
  }

  /** 明示操作でも直前のreadbackを照合する。失われたPOST応答を記憶から再送しない。 */
  async function continueOperation(): Promise<void> {
    await run(async () => {
      const op = await current();
      if (!op) {
        const kind = await verifyAccess(true);
        if (kind) { lastKind = kind; await drive(await reserve(kind), true); }
        else {
          update({ phase: "completed", operation: null, reason: null, busy: false,
            manualCompletion: state.manualCompletion + 1 });
        }
        return;
      }
      if (op?.status === "completed") {
        const kind = await verifyAccess(true);
        if (kind) { lastKind = kind; await drive(await reserve(kind), true); return; }
      }
      if (op?.status === "reserved" || op?.status === "token_received") await drive(op, true);
      else show(op, true);
    });
  }

  async function restart(): Promise<void> {
    const expected = state.operation?.operation_id;
    await run(async () => {
      const op = await current();
      if (!op || op.operation_id !== expected || !["failed", "expired"].includes(op.status)) {
        await showReadback(op, true); return;
      }
      await drive(await post(`/auth/operations/${op.operation_id}/restart`), true);
    });
  }

  /** 新installの証明はserverだけで判定する。unknownの通常restartとは別の明示操作。 */
  async function confirmInstallation(): Promise<void> {
    await run(async () => {
      if (!lastKind) { block("not_ready"); return; }
      await drive(await reserve(lastKind), true);
    });
  }

  /** 既存Shopの旧token使用禁止は、明示した回復操作で新tokenを採用して解消する。 */
  async function recoverToken(): Promise<void> {
    await run(async () => {
      const op = await current();
      if (op && op.status !== "completed") {
        if (op.status === "reserved" || op.status === "token_received") await drive(op, true);
        else show(op, true);
        return;
      }
      const kind = await verifyAccess(true);
      if (!kind && state.offlineTokenState === "ready") {
        update({ phase: "completed", operation: op, reason: null, busy: false,
          manualCompletion: state.manualCompletion + 1 });
        return;
      }
      lastKind = kind ?? "recover";
      await drive(await reserve(lastKind), true);
    });
  }

  return {
    subscribe(notify: () => void) { listeners.add(notify); return () => { listeners.delete(notify); }; },
    getSnapshot: () => state,
    getAccessRevision: () => accessRevision,
    // 通常のme要求は送信開始時の版を照合し、取消し後の成功を採用しない。
    acceptMeStatus: (me: { install_setup_status?: string | null; offline_token_state?: string | null }, revision: number) => acceptMeStatus(me, revision),
    ensure, observe, check, continueOperation, restart, confirmInstallation, recoverToken, block,
  };
}

export const authRecovery = createAuthRecovery();
